import React, { useRef, useState } from "react";

const AudioConverter = () => {
  const [bitDepth, setBitDepth] = useState(16);
  const audioCtxRef = useRef(new (window.AudioContext || window.webkitAudioContext)());
  const audioBufferRef = useRef(null);

  const originalAudioRef = useRef(null);
  const bitAudioRef = useRef(null);
  const originalCanvasRef = useRef(null);
  const bitCanvasRef = useRef(null);

  const drawWaveform = (buffer, canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const data = buffer.getChannelData(0);
    const step = Math.ceil(data.length / canvas.width);
    const amp = canvas.height / 2;
    ctx.beginPath();
    ctx.moveTo(0, amp);
    for (let i = 0; i < canvas.width; i++) {
      const min = Math.min(...data.slice(i * step, (i + 1) * step));
      const max = Math.max(...data.slice(i * step, (i + 1) * step));
      ctx.lineTo(i, (1 + min) * amp);
      ctx.lineTo(i, (1 + max) * amp);
    }
    ctx.strokeStyle = "#39FF14";
    ctx.stroke();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const arrayBuffer = await file.arrayBuffer();
    try {
      audioBufferRef.current = await audioCtxRef.current.decodeAudioData(arrayBuffer.slice(0));
    } catch (err) {
      alert("Error decoding audio: " + err.message);
      return;
    }

    const blob = new Blob([arrayBuffer], { type: file.type });
    const url = URL.createObjectURL(blob);

    if (originalAudioRef.current) {
      originalAudioRef.current.src = url;
      originalAudioRef.current.load();
      originalAudioRef.current.play(); // <-- Play original audio automatically
    }

    drawWaveform(audioBufferRef.current, originalCanvasRef.current);
  };

  const handleConvert = async () => {
    if (!audioBufferRef.current) return alert("Please upload a sound first!");

    const levels = Math.pow(2, bitDepth - 1) - 1;
    const audioBuffer = audioBufferRef.current;
    const processedBuffer = audioCtxRef.current.createBuffer(
      audioBuffer.numberOfChannels,
      audioBuffer.length,
      audioBuffer.sampleRate
    );

    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const input = audioBuffer.getChannelData(channel);
      const output = processedBuffer.getChannelData(channel);
      for (let i = 0; i < input.length; i++) {
        output[i] = Math.round(input[i] * levels) / levels;
      }
    }

    const wavBlob = await processedBufferToWav(processedBuffer);
    const url = URL.createObjectURL(wavBlob);
    if (bitAudioRef.current) {
      bitAudioRef.current.src = url;
      bitAudioRef.current.load();
      bitAudioRef.current.play(); // <-- Play converted audio automatically
    }

    drawWaveform(processedBuffer, bitCanvasRef.current);
  };

  const processedBufferToWav = async (abuffer) => {
    const numOfChan = abuffer.numberOfChannels;
    const length = abuffer.length * numOfChan * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);

    let pos = 0;
    function setUint16(data) {
      view.setUint16(pos, data, true);
      pos += 2;
    }
    function setUint32(data) {
      view.setUint32(pos, data, true);
      pos += 4;
    }

    setUint32(0x46464952);
    setUint32(length - 8);
    setUint32(0x45564157);

    setUint32(0x20746d66);
    setUint32(16);
    setUint16(1);
    setUint16(numOfChan);
    setUint32(abuffer.sampleRate);
    setUint32(abuffer.sampleRate * numOfChan * 2);
    setUint16(numOfChan * 2);
    setUint16(16);

    setUint32(0x61746164);
    setUint32(length - pos - 4);

    const channels = [];
    for (let i = 0; i < numOfChan; i++) channels.push(abuffer.getChannelData(i));

    for (let i = 0; i < abuffer.length; i++) {
      for (let ch = 0; ch < numOfChan; ch++) {
        let sample = Math.max(-1, Math.min(1, channels[ch][i]));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(pos, sample, true);
        pos += 2;
      }
    }

    return new Blob([buffer], { type: "audio/wav" });
  };

  // Inline styles
  const styles = {
    container: {
      fontFamily: "'Press Start 2P', cursive",
      background: "linear-gradient(135deg, #1a1a1a, #0d0d0d)",
      color: "#39FF14",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "20px",
      minHeight: "100vh",
    },
    retroBtn: {
      background: "#00FFFF",
      color: "#000",
      fontWeight: "bold",
      padding: "12px 24px",
      border: "3px solid #FF00FF",
      cursor: "pointer",
      marginTop: "10px",
      transition: "0.2s",
      borderRadius: "8px",
    },
    sliderLabel: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      marginTop: "10px",
      marginBottom: "5px",
    },
    bitDisplay: {
      textAlign: "center",
      marginBottom: "10px",
      fontSize: "18px",
    },
    audioSection: {
      background: "#0d0d0d",
      padding: "15px",
      border: "2px solid #39FF14",
      borderRadius: "12px",
      marginBottom: "20px",
      width: "100%",
      maxWidth: "800px",
    },
    canvas: {
      width: "100%",
      height: "100px",
      border: "1px solid #39FF14",
      borderRadius: "6px",
      marginTop: "10px",
    },
  };

  return (
    <div style={styles.container}>
      <h1 className="text-3xl mb-6">🎵 Audio Converter with Waveform</h1>

      <input
        type="file"
        accept="audio/*"
        onChange={handleFileUpload}
        className="mb-4 p-2 border-2 border-green-400 rounded"
      />

      <div style={styles.bitDisplay}>
        Selected Bit Depth: <span>{bitDepth}</span>
      </div>

      <div className="w-full max-w-lg mb-4">
        <div style={styles.sliderLabel}>
          <span>1-bit</span>
          <span>16-bit</span>
        </div>
        <input
          type="range"
          min="1"
          max="16"
          value={bitDepth}
          onChange={(e) => setBitDepth(parseInt(e.target.value))}
          className="w-full mb-4"
        />
      </div>

      <button onClick={handleConvert} style={styles.retroBtn}>
        Convert Audio
      </button>

      <div style={styles.audioSection}>
        <p>Original Audio</p>
        <audio ref={originalAudioRef} controls></audio>
        <canvas ref={originalCanvasRef} style={styles.canvas}></canvas>
      </div>

      <div style={styles.audioSection}>
        <p>Bit-Depth Audio</p>
        <audio ref={bitAudioRef} controls></audio>
        <canvas ref={bitCanvasRef} style={styles.canvas}></canvas>
      </div>
    </div>
  );
};

export default AudioConverter;
